import type React from "react";
import { useEffect } from "react";
import { useRouter } from "solito/router";
import { FullscreenSpinner, isWeb, usePathname, validToken, XStack, YStack } from "@medbillai/ui";
import { authenticatePathname, getStartedUrl } from "@medbillai/utils/urls";
import { GrantedWordmark } from "../../components/local-images/LocalImages";
import { useAuth } from "../../lib/auth";
import { useFlagContextIsReady } from "../../lib/flag/shared";
export type SignUpOnboardingLayoutProps = {
  maxWidth?: number;
  /**
   * Web only — renders a full-height hero image beside the form in a
   * two-column split (hidden on small screens).
   */
  heroImageSrc?: string;
  children?: React.ReactNode;
};
export const SignUpOnboardingLayout = ({
  children,
  heroImageSrc
}: SignUpOnboardingLayoutProps) => {
  const pathname = usePathname();
  const router = useRouter();
  const {
    isAuthenticated,
    isReady
  } = useAuth();
  // Monotonic — once reads have been authorized (or the 2s floor fired),
  // login/logout dips de-authorize flag reads without re-blocking this gate.
  const flagContextIsReady = useFlagContextIsReady();
  // Only skip the flag gate once authenticated: cold OAuth loads must wait for
  // the anonymous identify so useOnboardingFlowVariation isn't stuck at v2.0.
  // The post-login latch dip is what would otherwise remount → FORCED_LOGOUT.
  const skipFlagGateForAuthCallback = pathname === authenticatePathname() && isAuthenticated;

  // If the user is authenticated and the pathname is a skip sign up onboarding
  // url, we need to redirect them to the home page so they are not stuck on the
  // sign up onboarding flow.
  useEffect(() => {
    if (isAuthenticated && isReady && pathname === getStartedUrl()) {
      router.replace("/");
    }
  }, [isAuthenticated, isReady, pathname, router]);

  // Show spinner if auth/flag context not ready or if we're about to redirect authenticated users
  if (!isReady || !flagContextIsReady && !skipFlagGateForAuthCallback || isAuthenticated && isReady && pathname === getStartedUrl()) {
    return <FullscreenSpinner />;
  }
  const showHero = isWeb && heroImageSrc !== undefined && heroImageSrc !== "";
  return <YStack f={1} $platform-web={{
    // Phone: a scrolling page, not an app shell — ≥ the visible
    // viewport, growing with content. flexBasis auto so the reset's
    // #__next flex column sizes from content, not basis 0. Keyboard
    // rationale: OnboardingScreen.tsx.
    minHeight: "100dvh",
    flexBasis: "auto"
  }} $gtSm={{
    height: validToken("100vh"),
    overflow: "hidden"
  }} backgroundColor="$surface1" data-sentry-element="YStack" data-sentry-component="SignUpOnboardingLayout" data-sentry-source-file="layout.web.tsx">
      {showHero ?
    // Capped at the xl breakpoint and centered: past it the 44% hero (and
    // the form column around it) would keep scaling with the viewport.
    <XStack f={1} width="100%" maxWidth={1650} alignSelf="center" $platform-web={{
      flexBasis: "auto"
    }}>
          <YStack f={1} minWidth={0}>
            {children}
          </YStack>
          <YStack display="none" width="44%" padding="$sp4" paddingLeft="$sp2" $gtSm={{
        display: "flex"
      }}>
            <YStack f={1} borderRadius="$br6" overflow="hidden" backgroundColor="$backgroundSecondary" style={{
          backgroundImage: `url(${heroImageSrc})`,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }} />
          </YStack>
        </XStack> : children}
      {isWeb ? <YStack position="absolute" top={showHero ? "$sp4" : "$sp2"} left={showHero ? "$sp5" : "$sp2.5"} height="$si4" justifyContent="center" pointerEvents="none" zIndex={1}>
          <GrantedWordmark />
        </YStack> : null}
    </YStack>;
};